import {
  ColGroupDef,
  EditableCallbackParams,
  IRowNode,
} from 'ag-grid-community'
import { useMemo } from 'react'
import { i18nLabelVoService } from '../../../../../domain/value-object/I18nLabelVO'
import {
  WbsItemAdditionalPropertyValuesVO,
  WbsItemAdditionalPropertyValueType,
} from '../../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { WbsItemTypeVO } from '../../../../../domain/value-object/WbsItemTypeVO'
import { wbsItemAdditionalPropertyColumnDef } from './columnDef'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { WbsItemAdditionalPropertyEntity } from '../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { useEntitySearchReferenceEntityRepositories } from '../../../../hooks/useWbsItemAdditionalProperties'
import {
  BulkSheetColumnDef,
  BulkSheetColumnGroupDef,
  EntityExtensionExternalIdToWbsItemAdditionalPropertyUuid,
} from '../../model'
import { ApplicationFunctionPropertyConfiguration } from '../../../../../lib/commons/appFunction'
import { useWbsItemAdditionalPropertyRepository } from '../../../../../services/adaptors/wbsItemAdditionalPropertyRepositoryAdaptor'

export interface Option<RowData> {
  getAdditionalPropertyValues: (
    rowData: RowData
  ) => WbsItemAdditionalPropertyValuesVO | undefined
  setAdditionalPropertyValues: (
    rowData: IRowNode<RowData>,
    wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
    oldValue: WbsItemAdditionalPropertyValueType,
    newValue: WbsItemAdditionalPropertyValueType
  ) => void
  getWbsItemType: (rowData: RowData) => WbsItemTypeVO | undefined
  columnFilterType?: 'CLIENT_SIDE' | 'SERVER_SIDE'
  disableFloatingFilter?: boolean
}
type State<RowData> = {
  wbsItemAdditionalPropertyColumns: BulkSheetColumnGroupDef<RowData>[]
  entityExtensionExternalIdToWbsItemAdditionalPropertyUuid: EntityExtensionExternalIdToWbsItemAdditionalPropertyUuid
}
type RequiredPropertyConfigurations = {
  [externalId: string]: ApplicationFunctionPropertyConfiguration
}
export const useWbsItemAdditionalPropertyColumns = <RowData>(
  projectUuid,
  wbsItemAdditionalProperties:
    | WbsItemAdditionalPropertyLayoutEntity
    | undefined,
  option: Option<RowData>,
  propertyConfigurations: ApplicationFunctionPropertyConfiguration[]
): State<RowData> => {
  const wbsItemAdditionalPropertyRepository =
    useWbsItemAdditionalPropertyRepository()
  const { getEntitySearchReferenceEntityRepository } =
    useEntitySearchReferenceEntityRepositories(projectUuid)
  const requiredPropertyConfigurations =
    useMemo<RequiredPropertyConfigurations>(() => {
      const config: RequiredPropertyConfigurations = {}
      propertyConfigurations.forEach(v => {
        if (v.requiredIf) {
          config[v.externalId] = v
        }
      })
      return config
    }, [propertyConfigurations])
  const state = useMemo<State<RowData>>(() => {
    if (!wbsItemAdditionalProperties) {
      return {
        wbsItemAdditionalPropertyColumns: [],
        entityExtensionExternalIdToWbsItemAdditionalPropertyUuid: {},
      }
    }

    const entityExtensionExternalIdToWbsItemAdditionalPropertyUuid = {}
    const wbsItemAdditionalPropertyColumns =
      wbsItemAdditionalProperties.groupHeaderLayouts.map(
        ({ groupHeader, propertyLayouts }) => {
          if (groupHeader.entityExtensionExternalId) {
            entityExtensionExternalIdToWbsItemAdditionalPropertyUuid[
              groupHeader.entityExtensionExternalId
            ] = groupHeader.uuid
          }
          return {
            groupId: groupHeader.uuid,
            externalId: groupHeader.uuid,
            headerName: i18nLabelVoService.getLabel(groupHeader.headerNameI18n),
            children: propertyLayouts.map(({ wbsItemAdditionalProperty }) => {
              if (wbsItemAdditionalProperty.entityExtensionExternalId) {
                entityExtensionExternalIdToWbsItemAdditionalPropertyUuid[
                  wbsItemAdditionalProperty.entityExtensionExternalId
                ] = wbsItemAdditionalProperty.uuid
              }
              return wbsItemAdditionalPropertyColumnDef(
                projectUuid,
                wbsItemAdditionalProperty,
                wbsItemAdditionalPropertyRepository,
                option,
                getEntitySearchReferenceEntityRepository,
                wbsItemAdditionalProperty.entityExtensionExternalId
                  ? requiredPropertyConfigurations[
                      wbsItemAdditionalProperty.entityExtensionExternalId
                    ]
                  : undefined
              )
            }),
          } as BulkSheetColumnGroupDef<RowData>
        }
      )
    return {
      wbsItemAdditionalPropertyColumns,
      entityExtensionExternalIdToWbsItemAdditionalPropertyUuid,
    }
  }, [
    projectUuid,
    wbsItemAdditionalProperties,
    getEntitySearchReferenceEntityRepository,
    requiredPropertyConfigurations,
  ])

  return state
}

export const isAdditionalPropertyColumn = (
  column: BulkSheetColumnGroupDef | BulkSheetColumnDef
): boolean => {
  return Boolean(
    column.externalId?.match(
      /[a-z0-9]{8}\-[a-z0-9]{4}\-[a-z0-9]{4}\-[a-z0-9]{4}\-[a-z0-9]{12}/
    )
  )
}
